import { get, orderBy } from 'lodash';
import { createSelector } from 'reselect';
import moment from 'moment';
import { scheduleStatus, messageMap, genSummaryText , buildPolicyListTitle } from '@store/policies/schedule/utils';
import { policiesSelector } from '../precisionPolicy/selectors';
import SCOPE from './types';

const selector = state => get(state, SCOPE);

export const baseSelector = createSelector(selector, state => state.base);
export const formViewSelector = createSelector(selector, state => state.form);
export const listViewSelector = createSelector(selector, state => state.list);

export const activeTabSelector = createSelector(baseSelector, state => state.activeTab);
export const sortBySelector = createSelector(baseSelector, state => state.sortBy);

export const policyOptionsSelector = createSelector(
  policiesSelector, formViewSelector,
  (policies, { policies: selectedPolicies }) => {
    const options: any = [];

    // order by policy index order for multiselect
    const orderedPolicies = orderBy(policies, ['index'], ['asc']);

    orderedPolicies.forEach(p => {
      const versions: any = [];
      let isFilterSelected = false;

      p.versions.forEach(v => {
        const selected = selectedPolicies.includes(v.id);
        versions.push({
          id: v.id,
          title: v.name,
          filterId: p.id,
          selected,
          active: v.is_active,
        });
        isFilterSelected = isFilterSelected || selected;
      });
      options.push({ id: p.id, title: p.filter_name, versions, selected: isFilterSelected });
    });

    return options;
  });

export const policyLookupSelector = createSelector(
  policiesSelector, (policies) => {
    const lookup = {};
    policies.forEach(policy => {
      policy.versions.forEach(version => {
        lookup[version.id] = version;
      });
    });
    return lookup;
  });

export const scheduleListSelector = createSelector(
  listViewSelector,
  policyLookupSelector,
  sortBySelector,
  (state, policyLookup, sortBy) => {
    const { data, pagination } = get(state, ['response', 'data'], {
      data: { count: 0, rows: [] },
      pagination: null,
    });

    const { rows } = data;

    if (rows.length) {
      rows.forEach(r => {
        if (!(r.state in [scheduleStatus.pending, scheduleStatus.discarded])
          && r.runLog) {
          r.runSummary = genSummaryText(policyLookup, JSON.parse(r.runLog));
        }
        r.policyNames = buildPolicyListTitle(r.policyList)
      });
    }

    const sortedRows: any[] = orderBy(rows, [sortBy.key], [sortBy.order]);

    return { data: sortedRows, pagination, sortBy };
  });

export const listLoadingSelector = createSelector(listViewSelector, state => ({
  loading: state.isFetching,
  showError: state.errorCode !== 0 || state.errorMsg,
  errorMsg: state.errorMsg,
}));

export const scheduleCalendarSelector = createSelector(
  scheduleListSelector,
  ({ data }) => {
    return data.map(row => {
      return {
        ...row,
        title: buildPolicyListTitle(row.policyList),
        start: row.triggerAt,
        backgroundColor: `${messageMap[row.state] ? messageMap[row.state].backgroundColor : ''}`,
        classNames: row.state === scheduleStatus.pending ? [] : ['not-waiting-schedule'],
      }
    });
  });

export default selector;
